import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import { User, Mail, Shield, LogOut, ArrowLeft, Download, Map, ShoppingCart, Pencil, Lock, Check, X, Settings, PauseCircle, Trash2, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { LicenseSection } from '../components/LicenseSection';
import { BillingSection } from '../components/BillingSection';
import { ReferralDashboard } from '../components/ReferralDashboard';
import { NotificationBell } from '../components/NotificationBell';
import { API } from '../config';
import axios from 'axios';
import { toast } from 'sonner';

const TABS = [
  { id: 'overview', label: 'Mon compte' },
  { id: 'settings', label: 'Paramètres' },
];

export default function AccountPage() {
  const { t } = useI18n();
  const { user, loading, logout, getToken } = useAuth();
  const navigate = useNavigate();
  const [hasLicense, setHasLicense] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const headers = user ? { Authorization: `Bearer ${getToken()}` } : {};

  useEffect(() => {
    if (!loading && !user) navigate('/login');
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    const headers = { Authorization: `Bearer ${getToken()}` };
    axios.get(`${API}/license/status`, { headers })
      .then(r => setHasLicense((r.data.entitlements || []).length > 0))
      .catch(() => setHasLicense(false));
  }, [user, getToken]);

  if (loading) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!user) return null;

  return (
    <div className="min-h-screen bg-background" data-testid="account-page">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-500/3 via-transparent to-transparent" />
      <div className="relative max-w-2xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground text-sm transition-colors" data-testid="account-back-link">
            <ArrowLeft className="w-4 h-4" /> HexoPDF
          </Link>
          <NotificationBell />
        </div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="font-heading text-3xl font-bold text-foreground mb-4" data-testid="account-title">{t('account.title')}</h1>

          {/* Tabs */}
          <div className="flex gap-1 bg-secondary/50 rounded-xl p-1 mb-6" data-testid="account-tabs">
            {TABS.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.id ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid={`account-tab-${tab.id}`}>
                {tab.id === 'settings' && <Settings className="w-3.5 h-3.5 inline mr-1.5" />}
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'overview' && (
            <>
              {user.roles?.includes('admin') && (
                <Button variant="outline" className="w-full flex items-center justify-center gap-2 mb-4 border-border text-foreground/80 hover:bg-muted rounded-2xl py-3" onClick={() => navigate('/admin')} data-testid="account-admin-btn">
                  <Shield className="w-5 h-5" />
                  <span className="text-sm font-medium">Administration</span>
                </Button>
              )}
              {/* Profile Card (read-only overview) */}
              <div className="p-6 rounded-2xl bg-card border border-border mb-4" data-testid="account-profile-card">
                <h2 className="text-sm uppercase tracking-[0.15em] text-muted-foreground font-medium mb-4">{t('account.profile')}</h2>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center"><User className="w-4 h-4 text-blue-500" /></div>
                    <div>
                      <p className="text-foreground font-medium text-sm">{user.name || 'User'}</p>
                      <p className="text-muted-foreground text-xs">{user.roles?.join(', ')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center"><Mail className="w-4 h-4 text-muted-foreground" /></div>
                    <p className="text-foreground/80 text-sm">{user.email}</p>
                  </div>
                </div>
              </div>
              <div className="flex gap-3 mb-6">
                <Button variant="outline" className="flex-1 border-border text-foreground/80 hover:bg-muted rounded-lg" onClick={() => {
                  if (!hasLicense) { toast.error(t('account.no_license') || 'Aucune licence active.'); return; }
                  navigate('/downloads');
                }} data-testid="account-downloads-btn">
                  <Download className="w-4 h-4 mr-2" />{t('nav.downloads') || 'Downloads'}
                </Button>
                {!hasLicense && (
                  <Button className="flex-1 bg-blue-600 hover:bg-blue-500 text-white rounded-lg" onClick={() => navigate('/pricing')} data-testid="account-buy-license-btn">
                    <ShoppingCart className="w-4 h-4 mr-2" />{t('account.buy_license') || 'Acheter une licence'}
                  </Button>
                )}
                <Button variant="outline" className="flex-1 border-border text-foreground/80 hover:bg-muted rounded-lg" onClick={() => navigate('/changelog')} data-testid="account-roadmap-btn">
                  <Map className="w-4 h-4 mr-2" />{t('nav.roadmap') || 'Roadmap'}
                </Button>
              </div>
              <LicenseSection />
              <Separator className="bg-border my-6" />
              <BillingSection />
              <Separator className="bg-border my-6" />
              <ReferralDashboard />
            </>
          )}

          {activeTab === 'settings' && (
            <SettingsTab user={user} headers={headers} />
          )}

          <Separator className="bg-border my-6" />
          <Button variant="ghost" className="text-muted-foreground hover:text-red-500 transition-colors" onClick={() => { logout(); navigate('/'); }} data-testid="account-logout-btn">
            <LogOut className="w-4 h-4 mr-2" />{t('account.logout')}
          </Button>
        </motion.div>
      </div>
    </div>
  );
}

function SettingsTab({ user, headers }) {
  const { t } = useI18n();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [editingPassword, setEditingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [suspendModal, setSuspendModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);

  return (
    <div className="space-y-4">
      {/* Name */}
      <div className="p-5 rounded-2xl bg-card border border-border">
        <h2 className="text-sm uppercase tracking-[0.15em] text-muted-foreground font-medium mb-4">Nom d'utilisateur</h2>
        {editingName ? (
          <div className="flex items-center gap-2">
            <input value={newName} onChange={e => setNewName(e.target.value)} placeholder={user.name || 'Nom'}
              className="flex-1 px-3 py-2 rounded-lg bg-secondary/50 border border-border text-sm text-foreground" autoFocus data-testid="edit-name-input" />
            <button onClick={async () => {
              if (!newName.trim()) return;
              try {
                await axios.put(`${API}/me/profile`, { name: newName }, { headers });
                toast.success('Nom mis à jour');
                setEditingName(false);
                window.location.reload();
              } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
            }} className="p-2 rounded-lg hover:bg-emerald-500/10 text-emerald-500" data-testid="save-name-btn"><Check className="w-4 h-4" /></button>
            <button onClick={() => setEditingName(false)} className="p-2 rounded-lg hover:bg-red-500/10 text-red-400"><X className="w-4 h-4" /></button>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center"><User className="w-4 h-4 text-blue-500" /></div>
              <p className="text-foreground font-medium text-sm">{user.name || 'User'}</p>
            </div>
            <button onClick={() => { setNewName(user.name || ''); setEditingName(true); }}
              className="p-2 rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors" data-testid="edit-name-btn">
              <Pencil className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Password */}
      <div className="p-5 rounded-2xl bg-card border border-border">
        <h2 className="text-sm uppercase tracking-[0.15em] text-muted-foreground font-medium mb-4">Mot de passe</h2>
        {!editingPassword ? (
          <Button variant="outline" size="sm" className="border-border text-muted-foreground" onClick={() => setEditingPassword(true)} data-testid="change-password-btn">
            <Lock className="w-3.5 h-3.5 mr-1.5" /> {t('account.change_password') || 'Changer le mot de passe'}
          </Button>
        ) : (
          <div className="space-y-3" data-testid="password-form">
            <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)}
              placeholder={t('account.current_password') || 'Mot de passe actuel'}
              className="w-full px-3 py-2 rounded-lg bg-secondary/50 border border-border text-sm text-foreground" data-testid="current-password-input" />
            <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
              placeholder={t('account.new_password') || 'Nouveau mot de passe'}
              className="w-full px-3 py-2 rounded-lg bg-secondary/50 border border-border text-sm text-foreground" data-testid="new-password-input" />
            <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
              placeholder={t('account.confirm_password') || 'Confirmer le mot de passe'}
              className="w-full px-3 py-2 rounded-lg bg-secondary/50 border border-border text-sm text-foreground" data-testid="confirm-password-input" />
            <div className="flex gap-2">
              <Button size="sm" className="bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs" onClick={async () => {
                if (newPassword !== confirmPassword) { toast.error('Les mots de passe ne correspondent pas'); return; }
                if (newPassword.length < 6) { toast.error('Minimum 6 caractères'); return; }
                try {
                  await axios.put(`${API}/me/profile`, { current_password: currentPassword, new_password: newPassword }, { headers });
                  toast.success('Mot de passe mis à jour');
                  setEditingPassword(false); setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
                } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
              }} data-testid="save-password-btn">{t('account.save') || 'Enregistrer'}</Button>
              <Button size="sm" variant="ghost" className="text-muted-foreground text-xs" onClick={() => {
                setEditingPassword(false); setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
              }}>Annuler</Button>
            </div>
          </div>
        )}
      </div>

      {/* Danger Zone */}
      <div className="p-5 rounded-2xl bg-card border border-red-500/20">
        <h2 className="text-sm uppercase tracking-[0.15em] text-red-400 font-medium mb-4">Zone de danger</h2>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 rounded-xl bg-secondary/30">
            <div>
              <p className="text-foreground text-sm font-medium">Suspendre mon compte</p>
              <p className="text-muted-foreground text-xs">Votre compte et vos données sont conservés, mais les licences sont désactivées.</p>
            </div>
            <Button variant="outline" size="sm" className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 shrink-0"
              onClick={() => setSuspendModal(true)} data-testid="suspend-account-btn">
              <PauseCircle className="w-3.5 h-3.5 mr-1.5" /> Suspendre
            </Button>
          </div>
          <div className="flex items-center justify-between p-3 rounded-xl bg-secondary/30">
            <div>
              <p className="text-foreground text-sm font-medium">Supprimer mon compte</p>
              <p className="text-muted-foreground text-xs">Suppression irréversible de toutes vos données, licences et transactions.</p>
            </div>
            <Button variant="outline" size="sm" className="border-red-500/30 text-red-400 hover:bg-red-500/10 shrink-0"
              onClick={() => setDeleteModal(true)} data-testid="delete-account-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Supprimer
            </Button>
          </div>
        </div>
      </div>

      {suspendModal && (
        <AccountDangerModal
          type="suspend"
          onClose={() => setSuspendModal(false)}
          onConfirm={async (pwd) => {
            await axios.post(`${API}/me/suspend`, { password: pwd }, { headers });
            toast.success('Compte suspendu');
            setSuspendModal(false);
            logout();
            navigate('/');
          }}
        />
      )}
      {deleteModal && (
        <AccountDangerModal
          type="delete"
          onClose={() => setDeleteModal(false)}
          onConfirm={async (pwd) => {
            await axios.delete(`${API}/me/account`, { headers, data: { password: pwd } });
            toast.success('Compte supprimé');
            setDeleteModal(false);
            logout();
            navigate('/');
          }}
        />
      )}
    </div>
  );
}

function AccountDangerModal({ type, onClose, onConfirm }) {
  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [processing, setProcessing] = useState(false);
  const isSuspend = type === 'suspend';
  const expected = isSuspend ? 'SUSPENDRE' : 'SUPPRIMER';

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="danger-modal">
      <div className="bg-card border border-border rounded-xl max-w-sm w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isSuspend ? 'bg-amber-500/10' : 'bg-red-500/10'}`}>
            {isSuspend ? <PauseCircle className="w-5 h-5 text-amber-400" /> : <Trash2 className="w-5 h-5 text-red-400" />}
          </div>
          <h3 className="text-foreground font-medium text-sm">{isSuspend ? 'Suspendre mon compte' : 'Supprimer mon compte'}</h3>
          <button onClick={onClose} className="ml-auto text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>

        {!isSuspend && (
          <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/20 mb-4">
            <p className="text-red-400 text-xs flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Cette action est irréversible. Toutes vos données seront définitivement supprimées.</p>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="text-xs text-muted-foreground">Mot de passe de confirmation</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)}
              className="w-full mt-1 bg-secondary/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground"
              placeholder="Votre mot de passe" data-testid="danger-password" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">
              Tapez <span className="font-mono font-bold text-foreground">{expected}</span> pour confirmer
            </label>
            <input value={confirmText} onChange={e => setConfirmText(e.target.value)}
              className="w-full mt-1 bg-secondary/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground font-mono"
              placeholder={expected} data-testid="danger-confirm-input" />
          </div>
        </div>

        <div className="flex gap-2 mt-5">
          <Button variant="outline" className="flex-1 border-border text-muted-foreground" onClick={onClose}>Annuler</Button>
          <Button
            className={`flex-1 text-white ${isSuspend ? 'bg-amber-600 hover:bg-amber-700' : 'bg-red-600 hover:bg-red-700'}`}
            disabled={confirmText !== expected || !password || processing}
            onClick={async () => {
              setProcessing(true);
              try { await onConfirm(password); } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
              setProcessing(false);
            }}
            data-testid="danger-confirm-btn">
            {processing ? 'Traitement...' : isSuspend ? 'Suspendre' : 'Supprimer définitivement'}
          </Button>
        </div>
      </div>
    </div>
  );
}
